import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';

import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { getChannelEvents, getEventSessions } from '../../../../../store/actions/admin/analytics';
// import TabHeader from '../../../../general-ui/tab-header/tab-header';
import { capitalize } from "../../../../../utils/utils";
import { PATHNAMES } from "../../../admin";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import classNames from 'classnames';
import NavigationDropdown from '../../../../general-ui/dropdown/navigation-dropdown';
import { AnalyticsExportModal } from '../AnalyticsExportModal';
import { AnalyticsEvent, AnalyticsSession } from '../../../../../types/working-model';
import { Get } from '../../../../../connection/helpers';
import { showAlert } from '../../../../general-ui/alert/alert-service';


function PageHeader(): JSX.Element {
	const {
		AuthReducer: { user, token },
		AnalyticsReducer: { channelEvents, eventSessions }
	} = useTypedSelector(state => state);

	const dispatch = useDispatch();

	const { tab, eventUuid, sessionUuid }: { tab?: string, eventUuid?: string, sessionUuid?: string; } = useParams();

	useEffect(() => {
		if (eventUuid) dispatch(getChannelEvents(user!.active_channel, token!));
		if (eventUuid && sessionUuid) dispatch(getEventSessions(eventUuid, token!));
	}, [user, token, eventUuid, sessionUuid, dispatch]);

	// TODO: implement tabs (post mvp)
	const activeTab = tab ? capitalize(tab) : "Events";
	const [pathHistory, setPathHistory] = useState<string[]>([]);
	const [header, setHeader] = useState<string>();
	const [showExportModal, setShowExportModal] = useState<boolean>(false);
	const [event, setEvent] = useState<AnalyticsEvent>();
	const [session, setSession] = useState<AnalyticsSession>();

	useEffect(() => {
		enum StackLevelTypes {
			client = "Client",
			event = "Event",
			session = "Session"
		}
		const stackLevelType = sessionUuid ? StackLevelTypes.session : eventUuid ? StackLevelTypes.event : StackLevelTypes.client;
		const event = channelEvents.data.find(e => e.uuid === eventUuid);
		setEvent(event);
		const eventName = event?.name || '';
		const session = eventSessions.data.find(s => s.uuid === sessionUuid);
		setSession(session);
		const sessionName = session?.title?.base || '';

		switch (stackLevelType) {
			case StackLevelTypes.event:
				setHeader(eventName);
				setPathHistory([`All ${activeTab}`, eventName]);
				break;
			case StackLevelTypes.session:
				setHeader(sessionName);
				setPathHistory([`All ${activeTab}`, eventName, sessionName]);
				break;
			default:
				setHeader(`${activeTab} Analytics`);
				setPathHistory([]);

		}
	}, [activeTab, eventUuid, sessionUuid, channelEvents, eventSessions]);

	const EventHistory = (): JSX.Element => {
		return (<>
			<h6>/</h6>
			<h6 className="active">{pathHistory[1]}</h6>
		</>);
	};

	const SessionHistory = (): JSX.Element => {
		return (<>
			<h6>/</h6>
			<Link role="button" to={`${PATHNAMES.AnalyticsLink}/${tab}/${eventUuid}`}>
				<h6>{pathHistory[1]}</h6>
			</Link>
			<h6>/</h6>
			<h6 className="active">{pathHistory[2]}</h6>
		</>);
	};

	const RenderDownloadIcon = () => {
		return <Icon name={ICONS.CLOUD_DOWNLOAD} color={classNames(COLORS.BLACK, "icon")} size={14} />;
	};

	const downloadSummaryReport = () => {
		const exportUrl = sessionUuid ? `/v3/admin/metrics/sessions/${sessionUuid}/export/summary` : `/v3/admin/metrics/events/${eventUuid}/export/summary`;
		const eventOrSessionName = (session ? session?.title?.base : event?.name) || '';
		const date = new Date();
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const exportDate = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

		Get(`${exportUrl}?timeZone=${timeZone}`, token!, {}, true)
			.then(resp => {
				if (resp.ok) {
					return resp.blob();
				}
				throw ('Unable to fetch report');
			})
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = url;
				a.download = `${eventOrSessionName} Summary-${exportDate}.csv`.trim();
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
			})
			.catch(() => {
				showAlert({ message: "Download failed", description: "Your summary report failed to generate", duration: 3000, type: "error" });
			});
	};

	return (
		<nav className="navbar">
			{pathHistory.length > 0 && <div className="path-history">
				<Link role="button" to={`${PATHNAMES.AnalyticsLink}/${tab}`}>
					<h6><Icon name={ICONS.ARROW_LEFT} size={16} color={COLORS.ALT_GRAY} /></h6>
					<h6>{pathHistory[0]}</h6>
				</Link>
				{pathHistory.length === 2 && <EventHistory />}
				{pathHistory.length === 3 && <SessionHistory />}
			</div>}

			<div className="analytics-page-title-row">
				<h3 className="analytics-page-title">{header}</h3>
				{event &&
					<div>
						<NavigationDropdown title="Download report" buttonClassName="small primary" renderCustomIcon={RenderDownloadIcon} >
							<span onClick={downloadSummaryReport}>Summary report</span>
							<span onClick={() => setShowExportModal(true)}>Custom report</span>
						</NavigationDropdown>
						<AnalyticsExportModal open={showExportModal} onClose={() => setShowExportModal(false)} event={event} session={session} />
					</div>
				}
			</div>



			{/* FUTURE DEV - hiding tabs until post mvp, will need to add functionality for activeTab
			<div className="tab-wrapper">
				<div className="tab-container">
					<div className="tab-header">
						<TabHeader
							tabs={['Events', 'Surveys', 'Products']}
							defaultTab='Events'
							onClick={setActiveTab}
							currentTab={activeTab}
						/>
					</div>
				</div>
			</div> */}
		</nav>
	);
}

export default PageHeader;
